import { useEffect, useState } from 'react';
import {
  Alert, Button, Card, CardContent, Divider, FormControl, FormControlLabel,
  FormHelperText, Grid, InputAdornment, InputLabel, MenuItem, Select, Snackbar,
  Stack, Switch, TextField, Typography,
} from '@mui/material';
import { usePageTitle } from '../../components/shell/AppShell';
import type { CashDeskOptions } from '../../api/types';
import { useCashDeskOptions, useSaveCashDeskOptions } from '../../api/queries';
import { SitePicker } from './SitePicker';

/**
 * Per-site cash-desk options (plan Draft 1 W3 / owner rulings 6–8).
 * Semantics: the AFT master + cashier-transfers pair gates the desk's
 * line-of-sight transfer screen (this stage it gates the DESK only —
 * RM-initiated test-play is not affected). Payout method is the site
 * RULE: gift-card-only (COAM) vs cash-only. Changes reach the site
 * controller on its next config sync.
 */
export default function CashDeskOptionsPage() {
  usePageTitle('Cash desk options');
  const [siteId, setSiteId] = useState<string | null>(null);
  return (
    <Stack spacing={2}>
      <Typography variant="h4">Cash desk options</Typography>
      <SitePicker siteId={siteId} onChange={setSiteId} />
      {siteId && <CashDeskOptionsPanel siteId={siteId} />}
    </Stack>
  );
}

/** Site-scoped options form — embedded by the Sites page's tabbed detail
 *  view and wrapped with a picker by the standalone page above. */
export function CashDeskOptionsPanel({ siteId }: { siteId: string }) {
  const options = useCashDeskOptions(siteId);
  const save = useSaveCashDeskOptions(siteId);

  const [draft, setDraft] = useState<CashDeskOptions | null>(null);
  const [maxDollars, setMaxDollars] = useState('500');
  const [savedToast, setSavedToast] = useState(false);

  useEffect(() => {
    if (options.data) {
      setDraft(options.data);
      setMaxDollars((options.data.maxTransferCents / 100).toString());
    }
  }, [options.data]);

  const set = <K extends keyof CashDeskOptions>(key: K, value: CashDeskOptions[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const maxCents = Math.round(Number(maxDollars) * 100);
  const maxInvalid = !Number.isFinite(maxCents) || maxCents <= 0;
  const transfersBlocked = draft ? !draft.aftEnabled : false;

  return (
    <>
      {draft && (
        <Card sx={{ maxWidth: 860 }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="overline" color="text.secondary">
                How money moves at this site
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={draft.aftEnabled}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setDraft((d) => d
                        ? { ...d, aftEnabled: on, cashierTransfersEnabled: on ? d.cashierTransfersEnabled : false }
                        : d);
                    }}
                    inputProps={{ 'data-testid': 'opt-aft' } as object}
                  />
                }
                label={
                  <Stack>
                    <Typography>AFT machine transfers</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Master switch for AFT at this site's cash desk.
                    </Typography>
                  </Stack>
                }
              />

              <Stack sx={{ pl: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.cashierTransfersEnabled}
                      disabled={transfersBlocked}
                      onChange={(e) => set('cashierTransfersEnabled', e.target.checked)}
                      inputProps={{ 'data-testid': 'opt-cashier-transfers' } as object}
                    />
                  }
                  label={
                    <Stack>
                      <Typography>Cashier transfers to machines</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {transfersBlocked
                          ? 'Requires AFT machine transfers to be enabled.'
                          : 'Shows the line-of-sight transfer screen on the cash desk.'}
                      </Typography>
                    </Stack>
                  }
                />
              </Stack>

              <Stack sx={{ pl: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.transferOnWithCredits}
                      disabled={transfersBlocked}
                      onChange={(e) => set('transferOnWithCredits', e.target.checked)}
                      inputProps={{ 'data-testid': 'opt-on-with-credits' } as object}
                    />
                  }
                  label={
                    <Stack>
                      <Typography>Allow transfers onto a machine in play</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Off: a machine holding credits shows IN PLAY and cannot receive more money.
                      </Typography>
                    </Stack>
                  }
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={draft.cashierTransferOff}
                      disabled={transfersBlocked}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setDraft((d) => d
                          ? { ...d, cashierTransferOff: on,
                              cashierPartialTransferOff: on ? d.cashierPartialTransferOff : false }
                          : d);
                      }}
                      inputProps={{ 'data-testid': 'opt-transfer-off' } as object}
                    />
                  }
                  label={
                    <Stack>
                      <Typography>Cashier transfer OFF</Typography>
                      <Typography variant="caption" color="text.secondary">
                        The cashier can take money off a machine; it lands in the
                        cashouts list for payment.
                      </Typography>
                    </Stack>
                  }
                />
                <Stack sx={{ pl: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={draft.cashierPartialTransferOff}
                        disabled={transfersBlocked || !draft.cashierTransferOff}
                        onChange={(e) => set('cashierPartialTransferOff', e.target.checked)}
                        inputProps={{ 'data-testid': 'opt-partial-off' } as object}
                      />
                    }
                    label={
                      <Stack>
                        <Typography>Allow partial cash-off</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {draft.cashierTransferOff
                            ? 'The cashier can take SOME of the balance off, not just all of it.'
                            : 'Requires cashier transfer off to be enabled.'}
                        </Typography>
                      </Stack>
                    }
                  />
                </Stack>
              </Stack>

              <FormControlLabel
                control={
                  <Switch
                    checked={draft.titoEnabled}
                    onChange={(e) => set('titoEnabled', e.target.checked)}
                  />
                }
                label={
                  <Stack>
                    <Typography>TITO tickets</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Stored for later stages — ticket flows are not built yet.
                    </Typography>
                  </Stack>
                }
              />

              <Divider />

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="opt-payout-label">Redemptions</InputLabel>
                    <Select
                      labelId="opt-payout-label"
                      label="Redemptions"
                      value={draft.payoutMethod}
                      onChange={(e) => set('payoutMethod', e.target.value as CashDeskOptions['payoutMethod'])}
                    >
                      <MenuItem value="cash_only">Cash from the float</MenuItem>
                      <MenuItem value="gift_card_only">Gift-card redemptions only</MenuItem>
                    </Select>
                    <FormHelperText>
                      {draft.payoutMethod === 'gift_card_only'
                        ? 'COAM rule: all prizes load to a gift card — the desk pays no cash.'
                        : 'The desk pays prizes in cash from the float.'}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth size="small"
                    label="Max transfer"
                    value={maxDollars}
                    onChange={(e) => setMaxDollars(e.target.value)}
                    error={maxInvalid}
                    helperText={maxInvalid ? 'Enter a positive amount.' : 'Per-transfer cap at the desk.'}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    inputProps={{ 'data-testid': 'opt-max-transfer', inputMode: 'decimal' }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth size="small"
                    label="Cashout expiry (minutes)"
                    value={draft.redemptionExpiryMinutes}
                    onChange={(e) => set('redemptionExpiryMinutes',
                      Math.max(0, parseInt(e.target.value.replace(/\D/g, '') || '0', 10)))}
                    helperText="After this, only a manager can pay a pending cashout. 0 = never expires."
                    inputProps={{ 'data-testid': 'opt-redemption-expiry', inputMode: 'numeric' }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="opt-login-label">Desk login</InputLabel>
                    <Select
                      labelId="opt-login-label"
                      label="Desk login"
                      value={draft.loginMechanism}
                      onChange={(e) => set('loginMechanism', e.target.value as CashDeskOptions['loginMechanism'])}
                      inputProps={{ 'data-testid': 'opt-login-mechanism' } as object}
                    >
                      <MenuItem value="totp">Authenticator code</MenuItem>
                      <MenuItem value="totp_pin">Authenticator + in-shift PIN</MenuItem>
                      <MenuItem value="pin">PIN only</MenuItem>
                    </Select>
                    <FormHelperText>Verified on the site controller — works offline.</FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>

              {draft.loginMechanism === 'pin' && (
                <Alert severity="warning" data-testid="opt-pin-warning">
                  Reduced security: a PIN alone is a weak factor for cash movement. The
                  operator accepts this trade for the site.
                </Alert>
              )}

              <Divider />

              <Typography variant="overline" color="text.secondary">
                What the cashier sees with these settings
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                  <ExplainTile
                    ok={draft.aftEnabled && draft.cashierTransfersEnabled}
                    title="Machine transfers"
                    body={draft.aftEnabled && draft.cashierTransfersEnabled
                      ? 'Transfer screen shown — received cash can go onto any online machine.'
                      : 'Hidden — players insert bills at the machine instead.'}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ExplainTile
                    ok={draft.titoEnabled}
                    title="Ticket sales"
                    body={draft.titoEnabled
                      ? 'Enabled for a later stage — no desk flow yet.'
                      : 'Hidden — TITO is off.'}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ExplainTile
                    ok={draft.aftEnabled}
                    title="Cashouts"
                    body={!draft.aftEnabled
                      ? 'Hidden — AFT is off, so nothing drains to escrow.'
                      : draft.payoutMethod === 'gift_card_only'
                        ? 'Machine cashouts appear at every desk; each is loaded to a gift card (exact value, reference recorded).'
                        : 'Machine cashouts appear at every desk and are paid in cash from the float.'}
                  />
                </Grid>
              </Grid>

              {save.isError && (
                <Alert severity="error">
                  {(save.error as { message?: string })?.message ?? 'Save failed.'}
                </Alert>
              )}

              <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Applies on the site controller's next config sync.
                </Typography>
                <Button
                  variant="contained"
                  disabled={maxInvalid || save.isPending}
                  data-testid="opt-save"
                  onClick={() => save.mutate(
                    { ...draft, maxTransferCents: maxCents },
                    { onSuccess: () => setSavedToast(true) },
                  )}
                >
                  Save options
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Snackbar
        open={savedToast}
        autoHideDuration={4000}
        onClose={() => setSavedToast(false)}
        message="Cash desk options saved"
      />
    </>
  );
}

function ExplainTile({ ok, title, body }: { ok: boolean; title: string; body: string }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography variant="subtitle2" color={ok ? 'success.main' : 'text.disabled'}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">{body}</Typography>
      </CardContent>
    </Card>
  );
}
